import React from 'react';

/**
 * NatarajaWatermark - A subtle SVG watermark inspired by the prabha mandala
 * (ring of fire) surrounding the Nataraja sculpture.
 *
 * Uses concentric circles to create a cosmic, meditative background
 * that doesn't distract from the sacred text content.
 */

interface NatarajaWatermarkProps {
  className?: string;
}

const NatarajaWatermark: React.FC<NatarajaWatermarkProps> = ({ className = '' }) => {
  return (
    <div className={`nataraja-container ${className}`}>
      <svg
        className="nataraja-watermark"
        viewBox="0 0 800 800"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        {/* Outer flame ring - prabha mandala */}
        <circle
          cx="400"
          cy="400"
          r="380"
          stroke="currentColor"
          strokeWidth="0.5"
          opacity="0.15"
        />

        {/* Concentric rings representing cosmic cycles */}
        <circle
          cx="400"
          cy="400"
          r="340"
          stroke="currentColor"
          strokeWidth="0.5"
          opacity="0.12"
        />

        <circle
          cx="400"
          cy="400"
          r="300"
          stroke="currentColor"
          strokeWidth="0.5"
          opacity="0.1"
        />

        <circle
          cx="400"
          cy="400"
          r="260"
          stroke="currentColor"
          strokeWidth="0.5"
          opacity="0.08"
        />

        <circle
          cx="400"
          cy="400"
          r="220"
          stroke="currentColor"
          strokeWidth="0.5"
          opacity="0.06"
        />

        <circle
          cx="400"
          cy="400"
          r="180"
          stroke="currentColor"
          strokeWidth="0.5"
          opacity="0.05"
        />

        {/* Inner sanctum - the still point */}
        <circle
          cx="400"
          cy="400"
          r="140"
          stroke="currentColor"
          strokeWidth="0.3"
          opacity="0.04"
        />

        <circle
          cx="400"
          cy="400"
          r="100"
          stroke="currentColor"
          strokeWidth="0.3"
          opacity="0.03"
        />

        {/* Central point - the bindu */}
        <circle
          cx="400"
          cy="400"
          r="60"
          stroke="currentColor"
          strokeWidth="0.25"
          opacity="0.025"
        />

        <circle
          cx="400"
          cy="400"
          r="20"
          fill="currentColor"
          opacity="0.015"
        />

        {/* Subtle radial lines - flames of the prabha */}
        {[...Array(36)].map((_, i) => {
          const angle = (i * 10 * Math.PI) / 180;
          const innerRadius = 60;
          const outerRadius = 380;
          const x1 = 400 + innerRadius * Math.cos(angle);
          const y1 = 400 + innerRadius * Math.sin(angle);
          const x2 = 400 + outerRadius * Math.cos(angle);
          const y2 = 400 + outerRadius * Math.sin(angle);

          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="currentColor"
              strokeWidth="0.25"
              opacity="0.02"
            />
          );
        })}
      </svg>
    </div>
  );
};

export default NatarajaWatermark;
